import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { axiosInstance } from "../App";
import { ArrowLeft, Plus, Clock, MessageSquare, Loader2, Trash2, Calendar as CalendarIcon, User, Kanban, FileText, BookTemplate, List, CalendarDays, GitBranch, BarChart2, Filter, Package } from "lucide-react";
import { format } from "date-fns";
import { tr } from "date-fns/locale/tr";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import ProjectMembers from "../components/ProjectMembers";
import ProjectDocuments from "../components/ProjectDocuments";
import TaskChecklist from "../components/TaskChecklist";
import TaskTags from "../components/TaskTags";
import SubtaskList from "../components/SubtaskList";
import TaskDependencies from "../components/TaskDependencies";
import TaskRecurrence from "../components/TaskRecurrence";
import TemplateSelector from "../components/TemplateSelector";
import ListView from "../components/ListView";
import CalendarView from "../components/CalendarView";
import TimelineView from "../components/TimelineView";
import TaskResourceAssignment from "../components/TaskResourceAssignment";
import GanttView from "../components/GanttView";
import FilterDrawer from "../components/FilterDrawer";
import MentionInput, { renderMentions } from "../components/MentionInput";
import KanbanBoard from "../components/KanbanBoard";
import { useTranslation } from 'react-i18next';

function ProjectDetail() {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [taskForm, setTaskForm] = useState({
    title: "",
    description: "",
    priority: "medium",
    status: "todo",
    due_date: null,
    assignee_id: "",
    sprint_id: null,
    estimated_start_date: null,
    estimated_end_date: null,
    estimated_duration_days: null,
    estimated_duration_hours: null,
  });
  const [showDurationFields, setShowDurationFields] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      setCurrentUser(JSON.parse(userData));
    }
    fetchProject();
    fetchTasks();
    fetchUsers();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await axiosInstance.get(`/projects/${projectId}`);
      setProject(response.data);
      
      // Fetch sprints for this project
      const sprintsResponse = await axiosInstance.get(`/projects/${projectId}/sprints`);
      setSprints(sprintsResponse.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(t('notProjectMember'));
        setTimeout(() => navigate("/dashboard"), 2000);
      } else {
        toast.error(t('projectLoadFailed'));
      }
      navigate("/dashboard");
    }
  };

  const fetchTasks = async (filters = {}) => {
    try {
      const params = { ...filters };
      // Clean empty filters
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });
      
      const response = await axiosInstance.get(`/projects/${projectId}/tasks`, { params });
      setTasks(response.data);
    } catch (error) {
      toast.error(t('tasksLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = (filters) => {
    setActiveFilters(filters);
    fetchTasks(filters);
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get("/users");
      setUsers(response.data);
    } catch (error) {
      toast.error(t('usersLoadFailed'));
    }
  };

  const fetchComments = async (taskId) => {
    try {
      const response = await axiosInstance.get(`/tasks/${taskId}/comments`);
      setComments(response.data);
    } catch (error) {
      toast.error(t('commentsLoadFailed'));
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!taskForm.title || taskForm.title.trim() === '') {
      toast.error(t('taskTitleRequired'));
      return;
    }
    
    try {
      await axiosInstance.post("/tasks", {
        ...taskForm,
        project_id: projectId,
        due_date: taskForm.due_date ? taskForm.due_date.toISOString() : null,
        assignee_id: (taskForm.assignee_id && taskForm.assignee_id !== "none") ? taskForm.assignee_id : null,
        sprint_id: (taskForm.sprint_id && taskForm.sprint_id !== "none") ? taskForm.sprint_id : null,
        estimated_start_date: taskForm.estimated_start_date ? taskForm.estimated_start_date.toISOString() : null,
        estimated_end_date: taskForm.estimated_end_date ? taskForm.estimated_end_date.toISOString() : null,
        estimated_duration_days: taskForm.estimated_duration_days,
        estimated_duration_hours: taskForm.estimated_duration_hours,
      });
      toast.success(t('taskCreated'));
      setTaskDialogOpen(false);
      setTaskForm({ 
        title: "", 
        description: "", 
        priority: "medium", 
        status: "todo", 
        due_date: null, 
        assignee_id: "",
        sprint_id: null,
        estimated_start_date: null,
        estimated_end_date: null,
        estimated_duration_days: null,
        estimated_duration_hours: null,
      });
      setShowDurationFields(false);
      fetchTasks();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || t('taskCreationFailed');
      toast.error(errorMsg);
      console.error('Task creation error:', error.response?.data);
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await axiosInstance.put(`/tasks/${taskId}`, { status: newStatus });
      toast.success(t('statusUpdated'));
      fetchTasks();
    } catch (error) {
      toast.error(t('statusUpdateFailed'));
    }
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setDetailDialogOpen(true);
    fetchComments(task.id);
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      await axiosInstance.post("/comments", {
        task_id: selectedTask.id,
        content: newComment,
      });
      toast.success(t('commentAdded'));
      setNewComment("");
      fetchComments(selectedTask.id);
    } catch (error) {
      toast.error(t('commentAddFailed'));
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm(t('confirmDeleteTaskMsg'))) return;
    try {
      await axiosInstance.delete(`/tasks/${taskId}`);
      toast.success(t('taskDeleted'));
      setDetailDialogOpen(false);
      fetchTasks();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(t('cannotDeleteOnlyCreator'));
      } else {
        toast.error(t('taskDeleteFailed'));
      }
    }
  };

  const handleUpdateTimeSpent = async (taskId, minutes) => {
    try {
      const currentTask = tasks.find(t => t.id === taskId);
      await axiosInstance.put(`/tasks/${taskId}`, {
        time_spent: (currentTask.time_spent || 0) + minutes
      });
      toast.success(t('timeSaved'));
      fetchTasks();
      if (selectedTask?.id === taskId) {
        const updatedTask = await axiosInstance.get(`/tasks/${taskId}`);
        setSelectedTask(updatedTask.data);
      }
    } catch (error) {
      toast.error(t('timeSaveFailed'));
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "bg-red-100 text-red-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "low":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case "high":
        return "Yüksek";
      case "medium":
        return "Orta";
      case "low":
        return "Düşük";
      default:
        return priority;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "todo":
        return "Yapılacak";
      case "in-progress":
        return "Devam Ediyor";
      case "done":
        return "Tamamlandı";
      default:
        return status;
    }
  };

  const getUserById = (userId) => {
    return users.find(u => u.id === userId);
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const formatMinutes = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}s ${mins}dk`;
    }
    return `${mins}dk`;
  };

  // Get display date for task (actual if done, estimated otherwise)
  const getTaskDisplayDate = (task) => {
    // For completed tasks, show actual dates
    if (task.status === 'done' && task.actual_end_date) {
      return {
        startDate: task.actual_start_date,
        endDate: task.actual_end_date,
        label: 'Gerçekleşen',
        isActual: true
      };
    }
    // For pending tasks, show estimated or due date
    if (task.estimated_end_date) {
      return {
        startDate: task.estimated_start_date,
        endDate: task.estimated_end_date,
        label: 'Tahmini',
        isActual: false
      };
    }
    if (task.due_date) {
      return {
        startDate: task.created_at,
        endDate: task.due_date,
        label: 'Teslim',
        isActual: false
      };
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F8F9FA' }}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="flex items-center gap-4 w-full sm:w-auto">
              <Button variant="ghost" onClick={() => navigate("/dashboard")} data-testid="back-button" className="flex-shrink-0">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <img 
                src="/milestora-logo-light.png" 
                alt="Milestora" 
                className="h-10 w-auto object-contain flex-shrink-0"
              />
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl font-bold truncate" style={{ fontFamily: 'Manrope', color: '#1F2937' }}>
                  {project?.name}
                </h1>
                <p className="text-sm text-gray-500 mt-1 line-clamp-1">{project?.description}</p>
              </div>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button 
                variant="outline" 
                onClick={() => setFilterDrawerOpen(true)}
                className="gap-2 flex-shrink-0"
              >
                <Filter className="h-4 w-4" />
                <span className="hidden sm:inline">{t('filters')}</span>
                {Object.values(activeFilters).filter(v => v).length > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {Object.values(activeFilters).filter(v => v).length}
                  </Badge>
                )}
              </Button>
              <TemplateSelector
                projectId={projectId}
                onTaskCreated={fetchTasks}
                trigger={
                  <Button variant="outline" className="gap-2 flex-1 sm:flex-initial">
                    <BookTemplate className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('fromTemplate')}</span>
                  </Button>
                }
              />
              <Button onClick={() => setTaskDialogOpen(true)} className="gap-2 flex-1 sm:flex-initial" style={{ background: '#3B82F6' }} data-testid="create-task-button">
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">{t('newTask')}</span>
                <span className="sm:hidden">+</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Kanban Board and Members */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="kanban" className="w-full">
          <TabsList className="w-full overflow-x-auto flex lg:grid lg:grid-cols-8 mb-6 gap-1">
            <TabsTrigger value="kanban" className="flex items-center gap-2 flex-shrink-0">
              <Kanban className="h-4 w-4" />
              <span className="hidden sm:inline">{t('kanban')}</span>
            </TabsTrigger>
            <TabsTrigger value="list" className="flex items-center gap-2 flex-shrink-0">
              <List className="h-4 w-4" />
              <span className="hidden sm:inline">{t('list')}</span>
            </TabsTrigger>
            <TabsTrigger value="calendar" className="flex items-center gap-2 flex-shrink-0">
              <CalendarDays className="h-4 w-4" />
              <span className="hidden sm:inline">{t('calendar')}</span>
            </TabsTrigger>
            <TabsTrigger value="timeline" className="flex items-center gap-2 flex-shrink-0">
              <GitBranch className="h-4 w-4" />
              <span className="hidden sm:inline">{t('timeline')}</span>
            </TabsTrigger>
            <TabsTrigger value="gantt" className="flex items-center gap-2 flex-shrink-0">
              <BarChart2 className="h-4 w-4" />
              <span className="hidden sm:inline">{t('gantt')}</span>
            </TabsTrigger>
            <TabsTrigger value="resources" className="flex items-center gap-2 flex-shrink-0">
              <Package className="h-4 w-4" />
              <span className="hidden sm:inline">Kaynaklar</span>
            </TabsTrigger>
            <TabsTrigger value="members" className="flex items-center gap-2 flex-shrink-0">
              <User className="h-4 w-4" />
              <span className="hidden sm:inline">{t('membersTab')}</span>
            </TabsTrigger>
            <TabsTrigger value="documents" className="flex items-center gap-2 flex-shrink-0">
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">{t('documentsTab')}</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="kanban">
            <KanbanBoard
              tasks={tasks}
              users={users}
              onTaskClick={handleTaskClick}
              onStatusChange={handleStatusChange}
            />
          </TabsContent>

          <TabsContent value="list">
            <ListView 
              tasks={tasks}
              users={users}
              onTaskClick={handleTaskClick}
            />
          </TabsContent>

          <TabsContent value="calendar">
            <CalendarView
              tasks={tasks}
              users={users}
              onTaskClick={handleTaskClick}
              onSelectSlot={(slotInfo) => {
                setTaskForm({
                  ...taskForm,
                  due_date: slotInfo.start
                });
                setTaskDialogOpen(true);
              }}
            />
          </TabsContent>

          <TabsContent value="timeline">
            <TimelineView
              tasks={tasks}
              users={users}
              onTaskClick={handleTaskClick}
            />
          </TabsContent>

          <TabsContent value="gantt">
            <GanttView
              tasks={tasks}
              users={users}
              onTaskClick={handleTaskClick}
              onTasksUpdate={fetchTasks}
            />
          </TabsContent>

          <TabsContent value="members">
            <ProjectMembers 
              projectId={projectId} 
              currentUserId={currentUser?.id} 
              projectOwnerId={project?.owner_id}
            />
          </TabsContent>

          <TabsContent value="resources">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{t('projectResources')}</h3>
                <Button onClick={() => navigate(`/projects/${projectId}/resources`)} style={{ background: '#3B82F6' }}>
                  <Package className="h-4 w-4 mr-2" />
                  {t('manageResources')}
                </Button>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  {t('resourceManagementInfo')}
                </p>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="documents">
            <ProjectDocuments 
              projectId={projectId} 
              currentUserId={currentUser?.id}
            />
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Task Dialog */}
      <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-task-dialog">
          <DialogHeader>
            <DialogTitle>{t('createTask')}</DialogTitle>
            <DialogDescription>{t('enterTaskDetails')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateTask} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">{t('taskTitle')} <span className="text-red-500">*</span></Label>
              <Input
                id="title"
                value={taskForm.title}
                onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                placeholder={t('taskTitle')}
                required
                data-testid="task-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">{t('taskDescription')}</Label>
              <Textarea
                id="description"
                value={taskForm.description}
                onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                placeholder={t('taskDescription')}
                rows={3}
                data-testid="task-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="priority">{t('priority')}</Label>
                <Select
                  value={taskForm.priority}
                  onValueChange={(value) => setTaskForm({ ...taskForm, priority: value })}
                >
                  <SelectTrigger data-testid="task-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[9999]">
                    <SelectItem value="low">{t('low')}</SelectItem>
                    <SelectItem value="medium">{t('medium')}</SelectItem>
                    <SelectItem value="high">{t('high')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">{t('status')}</Label>
                <Select
                  value={taskForm.status}
                  onValueChange={(value) => setTaskForm({ ...taskForm, status: value })}
                >
                  <SelectTrigger data-testid="task-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[9999]">
                    <SelectItem value="todo">{t('todo')}</SelectItem>
                    <SelectItem value="in-progress">{t('inProgress')}</SelectItem>
                    <SelectItem value="done">{t('done')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('assignedTo')}</Label>
              <Select
                value={taskForm.assignee_id}
                onValueChange={(value) => setTaskForm({ ...taskForm, assignee_id: value })}
              >
                <SelectTrigger data-testid="task-assignee-select">
                  <SelectValue placeholder={t('selectUserOptional')} />
                </SelectTrigger>
                <SelectContent className="z-[9999]">
                  <SelectItem value="none">{t('unassigned')}</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {taskForm.assignee_id && taskForm.assignee_id !== "none" && getUserById(taskForm.assignee_id) && (
                <div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
                  <Avatar className="h-6 w-6">
                    <AvatarFallback className="text-xs bg-blue-100 text-blue-700">
                      {getInitials(getUserById(taskForm.assignee_id).name)}
                    </AvatarFallback>
                  </Avatar>
                  {getUserById(taskForm.assignee_id).name}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Sprint</Label>
              <Select
                value={taskForm.sprint_id || "none"}
                onValueChange={(value) => setTaskForm({ ...taskForm, sprint_id: value === "none" ? null : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('selectSprintOptional')} />
                </SelectTrigger>
                <SelectContent className="z-[9999]">
                  <SelectItem value="none">{t('noSprint')}</SelectItem>
                  {sprints.filter(s => s.status !== 'cancelled').map((sprint) => (
                    <SelectItem key={sprint.id} value={sprint.id}>
                      {sprint.name} ({sprint.status === 'active' ? 'Aktif' : sprint.status === 'completed' ? 'Tamamlandı' : 'Planlandı'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Son Tarih</Label>
              <Popover modal={true}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="task-due-date-picker">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {taskForm.due_date ? format(taskForm.due_date, "PPP", { locale: tr }) : t('selectDate')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent 
                  className="w-auto p-0" 
                  align="start" 
                  side="bottom"
                  sideOffset={5}
                  style={{ zIndex: 999999 }}
                >
                  <Calendar
                    mode="single"
                    selected={taskForm.due_date}
                    onSelect={(date) => setTaskForm({ ...taskForm, due_date: date })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Gantt Chart Tarih Alanları */}
            <div className="border-t pt-4 space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">{t('ganttPlanningOptional')}</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDurationFields(!showDurationFields)}
                >
                  {showDurationFields ? t('hide') : t('show')}
                </Button>
              </div>
              
              {showDurationFields && (
                <>
                  <div className="space-y-2">
                    <Label>{t('estimatedStartDate')}</Label>
                    <Popover modal={true}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {taskForm.estimated_start_date ? format(taskForm.estimated_start_date, "PPP", { locale: tr }) : t('defaultCreationDate')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent 
                        className="w-auto p-0" 
                        align="start" 
                        side="bottom"
                        sideOffset={5}
                        style={{ zIndex: 999999 }}
                      >
                        <Calendar
                          mode="single"
                          selected={taskForm.estimated_start_date}
                          onSelect={(date) => setTaskForm({ ...taskForm, estimated_start_date: date })}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('estimatedDurationDays')}</Label>
                      <Input
                        type="number"
                        min="0"
                        value={taskForm.estimated_duration_days || ""}
                        onChange={(e) => setTaskForm({ ...taskForm, estimated_duration_days: e.target.value ? parseInt(e.target.value) : null })}
                        placeholder="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('estimatedDurationHours')}</Label>
                      <Input
                        type="number"
                        min="0"
                        max="23"
                        value={taskForm.estimated_duration_hours || ""}
                        onChange={(e) => setTaskForm({ ...taskForm, estimated_duration_hours: e.target.value ? parseInt(e.target.value) : null })}
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('estimatedEndDate')}</Label>
                    <Popover modal={true}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {taskForm.estimated_end_date ? format(taskForm.estimated_end_date, "PPP", { locale: tr }) : t('durationOrManual')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent 
                        className="w-auto p-0" 
                        align="start" 
                        side="bottom"
                        sideOffset={5}
                        style={{ zIndex: 999999 }}
                      >
                        <Calendar
                          mode="single"
                          selected={taskForm.estimated_end_date}
                          onSelect={(date) => setTaskForm({ ...taskForm, estimated_end_date: date })}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="text-xs text-gray-500 bg-blue-50 p-2 rounded">
                    {t('durationAutoCalculateHint')}
                  </div>
                </>
              )}
            </div>
            
            <Button type="submit" className="w-full" style={{ background: '#3B82F6' }} data-testid="submit-task-button">
              {t('create')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Task Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6" data-testid="task-detail-dialog">
          {selectedTask && (
            <>
              <DialogHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <DialogTitle className="text-2xl" data-testid="task-detail-title">{selectedTask.title}</DialogTitle>
                    <DialogDescription className="sr-only">
                      {t('taskDetailsManagement')}
                    </DialogDescription>
                    <div className="flex gap-2 mt-2">
                      <Badge className={getPriorityColor(selectedTask.priority)}>
                        {getPriorityLabel(selectedTask.priority)}
                      </Badge>
                      <Badge variant="outline" data-testid="task-detail-status">{getStatusLabel(selectedTask.status)}</Badge>
                    </div>
                  </div>
                </div>
              </DialogHeader>

              {/* Gantt Action Buttons */}
              {selectedTask.assignee_id === currentUser?.id && (
                <div className="flex gap-2 pb-4 border-b">
                  {!selectedTask.actual_start_date && selectedTask.status === "todo" && (
                    <Button
                      onClick={async () => {
                        try {
                          await axiosInstance.post(`/tasks/${selectedTask.id}/start`);
                          toast.success("Göreve başlandı!");
                          fetchTasks();
                          const updatedTask = await axiosInstance.get(`/tasks/${selectedTask.id}`);
                          setSelectedTask(updatedTask.data);
                        } catch (error) {
                          toast.error("Başlatma başarısız");
                        }
                      }}
                      className="gap-2"
                      style={{ background: '#10B981' }}
                    >
                      <Clock className="h-4 w-4" />
                      {t('startTask')}
                    </Button>
                  )}
                  {selectedTask.actual_start_date && selectedTask.status !== "done" && (
                    <Button
                      onClick={async () => {
                        try {
                          await axiosInstance.post(`/tasks/${selectedTask.id}/complete`);
                          toast.success("Görev tamamlandı!");
                          fetchTasks();
                          const updatedTask = await axiosInstance.get(`/tasks/${selectedTask.id}`);
                          setSelectedTask(updatedTask.data);
                        } catch (error) {
                          toast.error("Tamamlama başarısız");
                        }
                      }}
                      className="gap-2"
                      style={{ background: '#3B82F6' }}
                    >
                      <MessageSquare className="h-4 w-4" />
                      {t('completeTask')}
                    </Button>
                  )}
                </div>
              )}

              <div className="space-y-6">
                {/* Description */}
                {selectedTask.description && (
                  <div>
                    <h4 className="font-semibold mb-2">{t('description')}</h4>
                    <p className="text-sm text-gray-600" data-testid="task-detail-description">{selectedTask.description}</p>
                  </div>
                )}

                {/* Info */}
                <div className="grid grid-cols-2 gap-4">
                  {selectedTask.due_date && (
                    <div>
                      <h4 className="font-semibold mb-1 text-sm">{t('dueDate')}</h4>
                      <p className="text-sm text-gray-600">
                        {format(new Date(selectedTask.due_date), "PPP", { locale: tr })}
                      </p>
                    </div>
                  )}
                  <div>
                    <h4 className="font-semibold mb-1 text-sm">{t('timeSpent')}</h4>
                    <p className="text-sm text-gray-600" data-testid="task-time-spent">
                      {selectedTask.time_spent > 0 ? formatMinutes(selectedTask.time_spent) : t('noTimeRecordedYet')}
                    </p>
                  </div>
                </div>

                {/* Assigned User */}
                <div>
                  <h4 className="font-semibold mb-2">{t('assignedPerson')}</h4>
                  <Select
                    value={selectedTask.assignee_id || "none"}
                    onValueChange={async (value) => {
                      try {
                        await axiosInstance.put(`/tasks/${selectedTask.id}`, {
                          assignee_id: value === "none" ? null : value
                        });
                        toast.success("Atama güncellendi");
                        fetchTasks();
                        const updatedTask = await axiosInstance.get(`/tasks/${selectedTask.id}`);
                        setSelectedTask(updatedTask.data);
                      } catch (error) {
                        toast.error("Atama güncellenemedi");
                      }
                    }}
                  >
                    <SelectTrigger data-testid="task-detail-assignee-select">
                      <SelectValue placeholder={t('selectUser')} />
                    </SelectTrigger>
                    <SelectContent className="z-[9999]">
                      <SelectItem value="none">{t('unassigned')}</SelectItem>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedTask.assignee_id && getUserById(selectedTask.assignee_id) && (
                    <div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-xs bg-blue-100 text-blue-700">
                          {getInitials(getUserById(selectedTask.assignee_id).name)}
                        </AvatarFallback>
                      </Avatar>
                      {getUserById(selectedTask.assignee_id).name}
                    </div>
                  )}
                </div>

                {/* Date Management */}
                <div className="space-y-4 border-t pt-4">
                  <h4 className="font-semibold text-sm sm:text-base">{t('dateAndTime')}</h4>
                  
                  {/* Estimated Dates */}
                  <div className="bg-blue-50 p-2 sm:p-3 rounded-lg space-y-2 sm:space-y-3">
                    <h5 className="text-xs sm:text-sm font-medium text-blue-900">{t('estimatedDates')}</h5>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                      <div>
                        <label className="text-xs text-gray-600 block mb-1">{t('estimatedStart')}</label>
                        <input
                          type="datetime-local"
                          className="w-full text-xs sm:text-sm p-1.5 sm:p-2 border rounded"
                          value={selectedTask.estimated_start_date ? new Date(selectedTask.estimated_start_date).toISOString().slice(0, 16) : ''}
                          onChange={async (e) => {
                            if (e.target.value) {
                              try {
                                await axiosInstance.put(`/tasks/${selectedTask.id}`, {
                                  estimated_start_date: new Date(e.target.value).toISOString()
                                });
                                toast.success("Tahmini başlangıç güncellendi");
                                const updatedTask = await axiosInstance.get(`/tasks/${selectedTask.id}`);
                                setSelectedTask(updatedTask.data);
                                fetchTasks();
                              } catch (error) {
                                toast.error("Güncelleme başarısız");
                              }
                            }
                          }}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 block mb-1">{t('estimatedEnd')}</label>
                        <input
                          type="datetime-local"
                          className="w-full text-xs sm:text-sm p-1.5 sm:p-2 border rounded"
                          value={selectedTask.estimated_end_date ? new Date(selectedTask.estimated_end_date).toISOString().slice(0, 16) : ''}
                          onChange={async (e) => {
                            if (e.target.value) {
                              try {
                                await axiosInstance.put(`/tasks/${selectedTask.id}`, {
                                  estimated_end_date: new Date(e.target.value).toISOString()
                                });
                                toast.success("Tahmini bitiş güncellendi");
                                const updatedTask = await axiosInstance.get(`/tasks/${selectedTask.id}`);
                                setSelectedTask(updatedTask.data);
                                fetchTasks();
                              } catch (error) {
                                toast.error("Güncelleme başarısız");
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Actual Dates */}
                  <div className="bg-green-50 p-2 sm:p-3 rounded-lg space-y-2 sm:space-y-3">
                    <h5 className="text-xs sm:text-sm font-medium text-green-900">{t('actualDates')}</h5>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                      <div>
                        <label className="text-xs text-gray-600 block mb-1">{t('actualStart')}</label>
                        <input
                          type="datetime-local"
                          className="w-full text-xs sm:text-sm p-1.5 sm:p-2 border rounded"
                          value={selectedTask.actual_start_date ? new Date(selectedTask.actual_start_date).toISOString().slice(0, 16) : ''}
                          onChange={async (e) => {
                            if (e.target.value) {
                              try {
                                await axiosInstance.put(`/tasks/${selectedTask.id}`, {
                                  actual_start_date: new Date(e.target.value).toISOString()
                                });
                                toast.success("Gerçekleşen başlangıç güncellendi");
                                const updatedTask = await axiosInstance.get(`/tasks/${selectedTask.id}`);
                                setSelectedTask(updatedTask.data);
                                fetchTasks();
                              } catch (error) {
                                toast.error("Güncelleme başarısız");
                              }
                            }
                          }}
                        />
                        {!selectedTask.actual_start_date && (
                          <p className="text-xs text-gray-500 mt-1">{t('notSet')}</p>
                        )}
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 block mb-1">{t('actualEnd')}</label>
                        <input
                          type="datetime-local"
                          className="w-full text-xs sm:text-sm p-1.5 sm:p-2 border rounded"
                          value={selectedTask.actual_end_date ? new Date(selectedTask.actual_end_date).toISOString().slice(0, 16) : ''}
                          onChange={async (e) => {
                            if (e.target.value) {
                              try {
                                await axiosInstance.put(`/tasks/${selectedTask.id}`, {
                                  actual_end_date: new Date(e.target.value).toISOString()
                                });
                                toast.success("Gerçekleşen bitiş güncellendi");
                                const updatedTask = await axiosInstance.get(`/tasks/${selectedTask.id}`);
                                setSelectedTask(updatedTask.data);
                                fetchTasks();
                              } catch (error) {
                                toast.error("Güncelleme başarısız");
                              }
                            }
                          }}
                        />
                        {!selectedTask.actual_end_date && (
                          <p className="text-xs text-gray-500 mt-1">{t('notSet')}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status Change */}
                <div>
                  <h4 className="font-semibold mb-2">Durum Değiştir</h4>
                  <div className="flex gap-2">
                    <Button
                      variant={selectedTask.status === "todo" ? "default" : "outline"}
                      onClick={() => handleStatusChange(selectedTask.id, "todo")}
                      className="flex-1"
                      data-testid="status-todo-button"
                    >
                      Yapılacak
                    </Button>
                    <Button
                      variant={selectedTask.status === "in-progress" ? "default" : "outline"}
                      onClick={() => handleStatusChange(selectedTask.id, "in-progress")}
                      className="flex-1"
                      data-testid="status-in-progress-button"
                    >
                      Devam Ediyor
                    </Button>
                    <Button
                      variant={selectedTask.status === "done" ? "default" : "outline"}
                      onClick={() => handleStatusChange(selectedTask.id, "done")}
                      className="flex-1"
                      data-testid="status-done-button"
                    >
                      Tamamlandı
                    </Button>
                  </div>
                </div>

                {/* Time Tracking */}
                <div>
                  <h4 className="font-semibold mb-2">{t('addTime')}</h4>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => handleUpdateTimeSpent(selectedTask.id, 15)} data-testid="add-time-15">
                      +15{t('minutesShort')}
                    </Button>
                    <Button variant="outline" onClick={() => handleUpdateTimeSpent(selectedTask.id, 30)} data-testid="add-time-30">
                      +30{t('minutesShort')}
                    </Button>
                    <Button variant="outline" onClick={() => handleUpdateTimeSpent(selectedTask.id, 60)} data-testid="add-time-60">
                      +1{t('hoursShort')}
                    </Button>
                    <Button variant="outline" onClick={() => handleUpdateTimeSpent(selectedTask.id, 120)} data-testid="add-time-120">
                      +2{t('hoursShort')}
                    </Button>
                  </div>
                </div>

                {/* Checklist */}
                <TaskChecklist
                  taskId={selectedTask.id}
                  checklist={selectedTask.checklist || []}
                  onUpdate={async () => {
                    await fetchTasks();
                    // Refetch the specific task to get updated data
                    try {
                      const response = await axiosInstance.get(`/tasks/${selectedTask.id}`);
                      setSelectedTask(response.data);
                    } catch (error) {
                      console.error("Failed to refetch task", error);
                    }
                  }}
                />

                {/* Tags */}
                <TaskTags
                  taskId={selectedTask.id}
                  tags={selectedTask.tags || []}
                  onUpdate={async () => {
                    await fetchTasks();
                    // Refetch the specific task to get updated data
                    try {
                      const response = await axiosInstance.get(`/tasks/${selectedTask.id}`);
                      setSelectedTask(response.data);
                    } catch (error) {
                      console.error("Failed to refetch task", error);
                    }
                  }}
                />

                {/* Resources */}
                <TaskResourceAssignment
                  taskId={selectedTask.id}
                  projectId={projectId}
                  onUpdate={async () => {
                    await fetchTasks();
                    try {
                      const response = await axiosInstance.get(`/tasks/${selectedTask.id}`);
                      setSelectedTask(response.data);
                    } catch (error) {
                      console.error("Failed to refetch task", error);
                    }
                  }}
                />

                {/* Subtasks */}
                <SubtaskList
                  taskId={selectedTask.id}
                  projectId={projectId}
                  projectMembers={project?.members || []}
                  onUpdate={async () => {
                    await fetchTasks();
                    // Refetch the specific task to get updated data
                    try {
                      const response = await axiosInstance.get(`/tasks/${selectedTask.id}`);
                      setSelectedTask(response.data);
                    } catch (error) {
                      console.error("Failed to refetch task", error);
                    }
                  }}
                />

                {/* Dependencies */}
                <TaskDependencies
                  taskId={selectedTask.id}
                  projectId={projectId}
                  onUpdate={async () => {
                    await fetchTasks();
                    // Refetch the specific task to get updated data
                    try {
                      const response = await axiosInstance.get(`/tasks/${selectedTask.id}`);
                      setSelectedTask(response.data);
                    } catch (error) {
                      console.error("Failed to refetch task", error);
                    }
                  }}
                />

                {/* Recurrence */}
                <TaskRecurrence
                  taskId={selectedTask.id}
                  isRecurring={selectedTask.is_recurring || false}
                  recurrencePattern={selectedTask.recurrence_pattern}
                  onUpdate={async () => {
                    await fetchTasks();
                    // Refetch the specific task to get updated data
                    try {
                      const response = await axiosInstance.get(`/tasks/${selectedTask.id}`);
                      setSelectedTask(response.data);
                    } catch (error) {
                      console.error("Failed to refetch task", error);
                    }
                  }}
                />

                {/* Comments */}
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    {t('comments')} ({comments.length})
                  </h4>
                  <div className="space-y-3 mb-4" data-testid="comments-section">
                    {comments.map((comment) => (
                      <div key={comment.id} className="bg-gray-50 rounded-lg p-3" data-testid={`comment-${comment.id}`}>
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-medium text-sm">{comment.user_name}</span>
                          <span className="text-xs text-gray-500">
                            {format(new Date(comment.created_at), "d MMM HH:mm", { locale: tr })}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          {renderMentions(comment.content, project?.members || [])}
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <MentionInput
                      placeholder={t('addComment') + ' (@kullanıcı ile etiketle)'}
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleAddComment();
                        }
                      }}
                      users={project?.members || []}
                      data-testid="comment-input"
                    />
                    <Button onClick={handleAddComment} data-testid="add-comment-button">
                      {t('send')}
                    </Button>
                  </div>
                </div>

                {/* Delete Task - Moved to bottom for safety - Only for creator */}
                {currentUser && selectedTask.created_by === currentUser.id && (
                  <div className="pt-4 border-t">
                    <Button
                      variant="destructive"
                      onClick={() => handleDeleteTask(selectedTask.id)}
                      data-testid="delete-task-button"
                      className="w-full flex items-center justify-center gap-2"
                    >
                      <Trash2 className="h-4 w-4" />
                      {t('deleteTask')}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Filter Drawer */}
      <FilterDrawer
        isOpen={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        onApplyFilters={handleApplyFilters}
        projectId={projectId}
      />
    </div>
  );
}

export default ProjectDetail;
